import { createContext, PropsWithChildren, useEffect, useState } from 'react'
import { ContextProps, InitialData } from './types'
import { useStorage } from '../../hooks/useStorage'
import { useDataForm } from './hooks/useDataForm'
import { useRegisters } from './hooks/useRegisters'

export const NecropsyContext = createContext({} as ContextProps)

export const NecropsyProvider = ({ children }: PropsWithChildren) => {
  const {
    form,
    setInitialForm,
    setCageSelection,
    setAditionalPhoto,
    setSinglePhoto,
    handleFinalForm,
    removeAditionalPhoto,
    resetForm,
    handleCenter,
  } = useDataForm()

  const {
    registers,
    saveRecord,
    changeStateForm,
    deleteRecords,
    initializerRecords,
    deleteObsoleteRecords,
    getDataByState,
    getRecordById,
  } = useRegisters()

  const [cages, setCages] = useState<{ [centerId: string]: { name: string; id: number }[] }>({})
  const [hasData, setHasData] = useState(false)
  const [initialData, setInitialData] = useState<InitialData[]>()

  useEffect(() => {
    if (form.stateForm !== null) {
      saveRecord(form)
    }
  }, [form.stateForm])

  useEffect(() => {
    deleteObsoleteRecords()
  }, [registers])

  const { isFirstRender, removeStorage } = useStorage({
    key: 'aqua-data-necropsy',
    initialData: {
      initialData,
      registers,
    },
    setDataFromStorage: (data) => {
      data.initialData && handleInitialData(data.initialData)
      initializerRecords(data.registers)
    },
  })

  const handleInitialData = (data: InitialData[]) => {
    isFirstRender.current = false
    setInitialData(data)
    setCages(
      Object.fromEntries(
        data.map((center) => [
          [center.id_centro],
          center.jaulas.map((cage) => ({
            name: cage.nombre,
            id: cage.id_jaula,
          })),
        ]),
      ),
    )
    setHasData(true)
    handleCenter(data)
  }

  const handleResetData = () => {
    setCages({})
    setHasData(false)
    setInitialData([])
    resetForm()
  }

  return (
    <NecropsyContext.Provider
      value={{
        setInitialForm,
        form,
        cages,
        registers,
        setCageSelection,
        setSinglePhoto,
        setAditionalPhoto,
        removeAditionalPhoto,
        handleFinalForm,
        resetForm,
        deleteRecords,
        getDataByState,
        handleInitialData,
        removeStorage,
        changeStateForm,
        hasData,
        handleResetData,
        getRecordById,
        deleteObsoleteRecords,
      }}
    >
      {children}
    </NecropsyContext.Provider>
  )
}
